import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Button, TextField, Chip,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox,
  ListItemText, SelectChangeEvent, Dialog, DialogTitle, DialogContent,
  DialogActions, Collapse, IconButton, Tooltip, InputAdornment, Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon, PriceChange as PriceIcon, Save as SaveIcon,
  Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Warning as WarningIcon, CheckCircle as CheckIcon, Clear as ClearIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../hooks/useGridEnterNavigation';

// ============================================
// Interfaces
// ============================================
interface BranchPriceDetail {
  branchId: number; branchName: string; branchNameAr: string;
  currentUnitPrice: number; quantity: number; affectedLines: number;
}
interface PricingSummaryItem {
  itemId: number; itemName: string; itemNameAr: string; itemCode: string;
  categoryId: number; categoryName: string; categoryNameAr: string;
  unitId: number; unitName: string; unitNameAr: string;
  currentUnitPrice: number; totalQuantity: number; affectedLines: number;
  branchPrices: BranchPriceDetail[];
}
interface PricingSummaryResult {
  fromDate: string; toDate: string; documentType: string;
  totalDocuments: number; totalLines: number;
  supplierName: string | null; custodyName: string | null;
  items: PricingSummaryItem[];
}
interface PriceUpdate { itemId: number; unitId: number; branchId: number | null; newPrice: number; }
interface Branch { branchId: number; branchName: string; branchNameAr: string; }
interface Supplier { supplierId: number; supplierCode: string; supplierName: string; supplierNameAr: string; }
interface Custody { custodyId: number; custodyNumber: string; employeeName: string; employeeNameAr: string; status: string; }
interface Category { categoryId: number; categoryName: string; categoryNameAr: string; }

// ============================================
// مكون بحث داخل القائمة المنسدلة
// ============================================
const DropdownSearch: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => (
  <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
    <TextField size="small" fullWidth placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value)} autoFocus
      onKeyDown={e => e.stopPropagation()}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
  </Box>
);

// ============================================
// Translations
// ============================================
const translations = {
  en: {
    title: 'Bulk Pricing', subtitle: 'Update prices for all purchase documents at once',
    fromDate: 'From Date', toDate: 'To Date', documentType: 'Document Type',
    purchaseOrders: 'Purchase Orders', purchaseInvoices: 'Purchase Invoices',
    branches: 'Branches', allBranches: 'All Branches',
    supplier: 'Supplier', allSuppliers: 'All Suppliers',
    custody: 'Custody', allCustodies: 'All Custodies',
    category: 'Category', allCategories: 'All Categories', searchItem: 'Search item...',
    search: 'Search', refresh: 'Refresh', selectAll: 'Select All', clear: 'Clear Filters',
    searchInList: 'Search...',
    item: 'Item', itemCode: 'Code', unit: 'Unit', currentPrice: 'Current Price',
    newPrice: 'New Price', totalQty: 'Total Qty', affectedLines: 'Lines',
    branchPricing: 'Branch Pricing', branch: 'Branch', quantity: 'Quantity',
    updateAll: 'Update All Prices', noData: 'No documents found',
    confirmTitle: 'Confirm Price Update', confirmMessage: 'Are you sure you want to update prices?',
    confirmDetails: 'documents and', confirmLines: 'lines will be affected',
    cancel: 'Cancel', confirm: 'Confirm Update', success: 'Prices updated successfully',
    noChanges: 'No price changes detected', kwd: 'KWD',
    totalDocs: 'Total Documents', totalLines: 'Total Lines',
    differentPricePerBranch: 'Different price per branch',
    noAccess: 'You do not have access to this page',
  },
  ar: {
    title: 'التسعير الجماعي', subtitle: 'تحديث أسعار جميع مستندات الشراء دفعة واحدة',
    fromDate: 'من تاريخ', toDate: 'إلى تاريخ', documentType: 'نوع المستند',
    purchaseOrders: 'أوامر الشراء', purchaseInvoices: 'فواتير الشراء',
    branches: 'الفروع', allBranches: 'كل الفروع',
    supplier: 'المورد', allSuppliers: 'كل الموردين',
    custody: 'العهدة', allCustodies: 'كل العهد',
    category: 'الفئة', allCategories: 'كل الفئات', searchItem: 'بحث عن صنف...',
    search: 'بحث', refresh: 'تحديث', selectAll: 'اختيار الكل', clear: 'مسح الفلاتر',
    searchInList: 'بحث...',
    item: 'الصنف', itemCode: 'الكود', unit: 'الوحدة', currentPrice: 'السعر الحالي',
    newPrice: 'السعر الجديد', totalQty: 'الكمية', affectedLines: 'الأسطر',
    branchPricing: 'التسعير حسب الفرع', branch: 'الفرع', quantity: 'الكمية',
    updateAll: 'تحديث جميع الأسعار', noData: 'لا توجد مستندات',
    confirmTitle: 'تأكيد تحديث الأسعار', confirmMessage: 'هل أنت متأكد من تحديث الأسعار؟',
    confirmDetails: 'مستند و', confirmLines: 'سطر سيتأثرون',
    cancel: 'إلغاء', confirm: 'تأكيد التحديث', success: 'تم تحديث الأسعار بنجاح',
    noChanges: 'لم يتم اكتشاف تغييرات', kwd: 'د.ك',
    totalDocs: 'عدد المستندات', totalLines: 'عدد الأسطر',
    differentPricePerBranch: 'سعر مختلف لكل فرع',
    noAccess: 'ليس لديك صلاحية لهذه الصفحة',
  }
};

const BulkPricingPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const isRtl = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const { user, permissions } = useAppSelector((state) => state.auth);

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const isBranchManager = user && !user.isSuperAdmin && user.roles?.some((r: string) =>
    r.includes('Branch Manager') || r.includes('مدير فرع'));
  const canSeeInvoices = user?.isSuperAdmin ||
    permissions.some((p: string) => ['ACCOUNTING_VIEW', 'JOURNAL_VIEW', 'JOURNAL_CREATE'].includes(p));

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [documentType, setDocumentType] = useState('PurchaseOrders');
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [custodyId, setCustodyId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');

  // بحث داخل القوائم
  const [branchSearch, setBranchSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [custodySearch, setCustodySearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [data, setData] = useState<PricingSummaryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newPrices, setNewPrices] = useState<Record<string, number>>({});

  // فلترة القوائم بالبحث
  const filteredBranches = useMemo(() => {
    if (!branchSearch) return branches;
    const s = branchSearch.toLowerCase();
    return branches.filter(b => b.branchName.toLowerCase().includes(s) || b.branchNameAr.includes(s));
  }, [branches, branchSearch]);

  const filteredSuppliers = useMemo(() => {
    if (!supplierSearch) return suppliers;
    const s = supplierSearch.toLowerCase();
    return suppliers.filter(sp => sp.supplierName.toLowerCase().includes(s) || (sp.supplierNameAr || '').includes(s) || sp.supplierCode.toLowerCase().includes(s));
  }, [suppliers, supplierSearch]);

  const filteredCustodies = useMemo(() => {
    if (!custodySearch) return custodies;
    const s = custodySearch.toLowerCase();
    return custodies.filter(c => c.custodyNumber.toLowerCase().includes(s) || c.employeeName.toLowerCase().includes(s) || (c.employeeNameAr || '').includes(s));
  }, [custodies, custodySearch]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch) return categories;
    const s = categorySearch.toLowerCase();
    return categories.filter(c => c.categoryName.toLowerCase().includes(s) || c.categoryNameAr.includes(s));
  }, [categories, categorySearch]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [brRes, supRes, cusRes, catRes] = await Promise.all([
          axiosInstance.get('/Branches'), axiosInstance.get('/Suppliers'),
          axiosInstance.get('/Custodies'), axiosInstance.get('/Categories'),
        ]);
        setBranches(brRes.data.filter?.((b: any) => b.isActive) || brRes.data);
        setSuppliers(supRes.data);
        setCustodies(cusRes.data.filter?.((c: any) => c.status === 'Open') || cusRes.data);
        setCategories(catRes.data.filter?.((c: any) => c.isActive !== false) || catRes.data);
      } catch { console.error('Failed to fetch lookups'); }
    };
    fetchLookups();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = `fromDate=${fromDate}&toDate=${toDate}&documentType=${documentType}`;
      if (selectedBranches.length > 0) params += `&branchIds=${selectedBranches.join(',')}`;
      if (supplierId) params += `&supplierId=${supplierId}`;
      if (custodyId) params += `&custodyId=${custodyId}`;
      if (categoryId) params += `&categoryId=${categoryId}`;
      if (searchText.trim()) params += `&search=${encodeURIComponent(searchText.trim())}`;
      const response = await axiosInstance.get(`/PurchaseConsolidation/pricing-summary?${params}`);
      setData(response.data);
      setNewPrices({});
      setExpandedRows(new Set());
    } catch (error: any) {
      if (error.response?.status === 403) toast.error(t.noAccess);
      else toast.error(error.response?.data?.message || 'Error');
    } finally { setLoading(false); }
  }, [fromDate, toDate, documentType, selectedBranches, supplierId, custodyId, categoryId, searchText]);

  useEffect(() => { if (!isBranchManager) fetchData(); }, []);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    if (value.includes(-1)) {
      setSelectedBranches(selectedBranches.length === branches.length ? [] : branches.map(b => b.branchId));
    } else { setSelectedBranches(value); }
  };

  const clearFilters = () => {
    setSelectedBranches([]); setSupplierId(''); setCustodyId('');
    setCategoryId(''); setSearchText('');
  };

  const handlePriceChange = (itemId: number, unitId: number, value: string) => {
    const key = `${itemId}-${unitId}`;
    const price = parseFloat(value);
    if (!isNaN(price) && price >= 0) setNewPrices(prev => ({ ...prev, [key]: price }));
    else if (value === '') setNewPrices(prev => { const u = { ...prev }; delete u[key]; return u; });
  };

  const handleBranchPriceChange = (itemId: number, unitId: number, branchId: number, value: string) => {
    const key = `${itemId}-${unitId}-${branchId}`;
    const price = parseFloat(value);
    if (!isNaN(price) && price >= 0) setNewPrices(prev => ({ ...prev, [key]: price }));
    else if (value === '') setNewPrices(prev => { const u = { ...prev }; delete u[key]; return u; });
  };

  const buildPriceUpdates = (): PriceUpdate[] => {
    const updates: PriceUpdate[] = [];
    if (!data) return updates;
    for (const item of data.items) {
      const gk = `${item.itemId}-${item.unitId}`;
      let hasBranch = false;
      for (const bp of item.branchPrices) {
        const bk = `${item.itemId}-${item.unitId}-${bp.branchId}`;
        if (newPrices[bk] !== undefined) {
          updates.push({ itemId: item.itemId, unitId: item.unitId, branchId: bp.branchId, newPrice: newPrices[bk] });
          hasBranch = true;
        }
      }
      if (newPrices[gk] !== undefined && !hasBranch)
        updates.push({ itemId: item.itemId, unitId: item.unitId, branchId: null, newPrice: newPrices[gk] });
    }
    return updates;
  };

  const handleUpdateClick = () => {
    if (buildPriceUpdates().length === 0) { toast.warning(t.noChanges); return; }
    setConfirmOpen(true);
  };

  const handleConfirmUpdate = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const updates = buildPriceUpdates();
      const response = await axiosInstance.put('/PurchaseConsolidation/bulk-pricing', {
        fromDate, toDate, documentType,
        branchIds: selectedBranches.length > 0 ? selectedBranches : null,
        supplierId: supplierId || null, custodyId: custodyId || null,
        categoryId: categoryId || null, items: updates
      });
      if (response.data.success) {
        toast.success(`${t.success} — ${response.data.updatedLines} ${isRtl ? 'سطر' : 'lines'}`);
        await fetchData();
      } else toast.error(response.data.message);
    } catch (error: any) { toast.error(error.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const changedCount = buildPriceUpdates().length;

  if (isBranchManager) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="error" variant="h6">{t.noAccess}</Typography></Box>;
  }

  return (
    <Box sx={{ p: 3 }} dir={isRtl ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <PriceIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Box>

      {/* ========== الفلاتر ========== */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.fromDate}
            value={fromDate ? new Date(fromDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setFromDate(newVal);
              if (toDate && newVal && toDate < newVal) setToDate('');
            }}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={toDate ? new Date(toDate) : maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.toDate}
            value={toDate ? new Date(toDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setToDate(newVal);
              if (fromDate && newVal && fromDate > newVal) setFromDate('');
            }}
            format="dd/MM/yyyy"
            minDate={fromDate ? new Date(fromDate) : minDate}
            maxDate={maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>

        {/* نوع المستند */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t.documentType}</InputLabel>
          <Select value={documentType} onChange={e => setDocumentType(e.target.value)} label={t.documentType}>
            <MenuItem value="PurchaseOrders">{t.purchaseOrders}</MenuItem>
            {canSeeInvoices && <MenuItem value="PurchaseInvoices">{t.purchaseInvoices}</MenuItem>}
          </Select>
        </FormControl>

        {/* الفروع */}
        <Autocomplete
          multiple
          size="small"
          options={branches}
          getOptionLabel={(o) => isRtl ? (o.branchNameAr || o.branchName) : o.branchName}
          value={branches.filter(b => selectedBranches.includes(b.branchId))}
          onChange={(_, v) => setSelectedBranches(v.map(b => b.branchId))}
          renderInput={(params) => <TextField {...params} label={t.branches} />}
          sx={{ minWidth: 250 }}
          limitTags={2}
        />

        {/* المورد */}
        <Autocomplete
          size="small"
          options={suppliers}
          getOptionLabel={(o) => `${o.supplierCode} - ${isRtl ? (o.supplierNameAr || o.supplierName) : o.supplierName}`}
          value={suppliers.find(s => s.supplierId === supplierId) || null}
          onChange={(_, v) => setSupplierId(v?.supplierId || '')}
          renderInput={(params) => <TextField {...params} label={t.supplier} />}
          sx={{ minWidth: 220 }}
        />

        {/* العهدة */}
        <Autocomplete
          size="small"
          options={custodies}
          getOptionLabel={(o) => `${o.custodyNumber} - ${isRtl ? (o.employeeNameAr || o.employeeName) : o.employeeName}`}
          value={custodies.find(c => c.custodyId === custodyId) || null}
          onChange={(_, v) => setCustodyId(v?.custodyId || '')}
          renderInput={(params) => <TextField {...params} label={t.custody} />}
          sx={{ minWidth: 240 }}
        />

        {/* الفئة */}
        <Autocomplete
          size="small"
          options={categories}
          getOptionLabel={(o) => isRtl ? (o.categoryNameAr || o.categoryName) : o.categoryName}
          value={categories.find(c => c.categoryId === categoryId) || null}
          onChange={(_, v) => setCategoryId(v?.categoryId || '')}
          renderInput={(params) => <TextField {...params} label={t.category} />}
          sx={{ minWidth: 200 }}
        />

        {/* بحث نصي */}
        <TextField size="small" placeholder={t.searchItem} value={searchText} onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 160 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

        <Button variant="contained" startIcon={<SearchIcon />} onClick={fetchData} disabled={loading}>{t.search}</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} size="small">{t.clear}</Button>
      </Paper>

      {/* ========== الملخص + زر التحديث ========== */}
      {data && data.items.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={`${t.totalDocs}: ${data.totalDocuments}`} color="primary" variant="outlined" />
          <Chip label={`${t.totalLines}: ${data.totalLines}`} color="info" variant="outlined" />
          {changedCount > 0 && <Chip label={`${changedCount} ${isRtl ? 'تغيير' : 'changes'}`} color="warning" icon={<WarningIcon />} />}
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" color="success" size="large" startIcon={<SaveIcon />}
            onClick={handleUpdateClick} disabled={changedCount === 0 || saving}>
            {saving ? <CircularProgress size={20} /> : t.updateAll} ({changedCount})
          </Button>
        </Box>
      )}

      {/* ========== الجدول ========== */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : data && data.items.length > 0 ? (
        <Box ref={gridRef}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' } }}>
                <TableCell width={40} /><TableCell>{t.itemCode}</TableCell><TableCell>{t.item}</TableCell>
                <TableCell>{t.category}</TableCell><TableCell>{t.unit}</TableCell>
                <TableCell align="center">{t.totalQty}</TableCell><TableCell align="right">{t.currentPrice}</TableCell>
                <TableCell align="center" sx={{ minWidth: 160 }}>{t.newPrice}</TableCell><TableCell align="center">{t.affectedLines}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(item => {
                const key = `${item.itemId}-${item.unitId}`;
                const isExp = expandedRows.has(key);
                const gp = newPrices[key]; const changed = gp !== undefined && gp !== item.currentUnitPrice;
                return (
                  <React.Fragment key={key}>
                    <TableRow hover sx={{ backgroundColor: changed ? 'rgba(255,152,0,0.08)' : 'inherit' }}>
                      <TableCell><Tooltip title={t.branchPricing}><IconButton size="small" onClick={() => toggleRow(key)}>{isExp ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton></Tooltip></TableCell>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{isRtl ? item.itemNameAr : item.itemName}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{isRtl ? item.categoryNameAr : item.categoryName}</TableCell>
                      <TableCell>{isRtl ? item.unitNameAr : item.unitName}</TableCell>
                      <TableCell align="center">{item.totalQuantity.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{item.currentUnitPrice.toFixed(3)}</TableCell>
                      <TableCell align="center">
                        <TextField size="small" placeholder={item.currentUnitPrice.toFixed(3)}
                          value={gp !== undefined ? gp : ''}
                          onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handlePriceChange(item.itemId, item.unitId, v); }}
                          sx={{ width: 140 }} inputProps={{ inputMode: 'decimal' }}
                          InputProps={{ endAdornment: changed ? <InputAdornment position="end"><CheckIcon color="success" fontSize="small" /></InputAdornment> : null }} />
                      </TableCell>
                      <TableCell align="center"><Chip label={item.affectedLines} size="small" /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isExp} timeout="auto" unmountOnExit>
                          <Box sx={{ mx: 4, my: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{t.differentPricePerBranch}</Typography>
                            <Table size="small">
                              <TableHead><TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'action.hover', fontSize: '0.8rem' } }}>
                                <TableCell>{t.branch}</TableCell><TableCell align="center">{t.quantity}</TableCell>
                                <TableCell align="right">{t.currentPrice}</TableCell><TableCell align="center" sx={{ minWidth: 140 }}>{t.newPrice}</TableCell>
                                <TableCell align="center">{t.affectedLines}</TableCell>
                              </TableRow></TableHead>
                              <TableBody>
                                {item.branchPrices.map(bp => {
                                  const bk = `${item.itemId}-${item.unitId}-${bp.branchId}`;
                                  const bprice = newPrices[bk]; const bc = bprice !== undefined && bprice !== bp.currentUnitPrice;
                                  return (
                                    <TableRow key={bp.branchId} sx={{ backgroundColor: bc ? 'rgba(255,152,0,0.08)' : 'inherit' }}>
                                      <TableCell>{isRtl ? bp.branchNameAr : bp.branchName}</TableCell>
                                      <TableCell align="center">{bp.quantity.toFixed(2)}</TableCell>
                                      <TableCell align="right">{bp.currentUnitPrice.toFixed(3)}</TableCell>
                                      <TableCell align="center">
                                        <TextField size="small" placeholder={bp.currentUnitPrice.toFixed(3)}
                                          value={bprice !== undefined ? bprice : ''}
                                          onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handleBranchPriceChange(item.itemId, item.unitId, bp.branchId, v); }}
                                          sx={{ width: 130 }} inputProps={{ inputMode: 'decimal' }}
                                          InputProps={{ endAdornment: bc ? <InputAdornment position="end"><CheckIcon color="success" fontSize="small" /></InputAdornment> : null }} />
                                      </TableCell>
                                      <TableCell align="center"><Chip label={bp.affectedLines} size="small" variant="outlined" /></TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      ) : data ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">{t.noData}</Typography></Paper>
      ) : null}

      {/* ========== Dialog تأكيد ========== */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><WarningIcon color="warning" />{t.confirmTitle}</DialogTitle>
        <DialogContent>
          <Typography>{t.confirmMessage}</Typography>
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2"><strong>{data?.totalDocuments}</strong> {t.confirmDetails} <strong>{data?.totalLines}</strong> {t.confirmLines}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}><strong>{changedCount}</strong> {isRtl ? 'صنف سيتم تحديث سعره' : 'item prices will be updated'}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t.cancel}</Button>
          <Button variant="contained" color="success" onClick={handleConfirmUpdate} startIcon={<SaveIcon />}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkPricingPage;